import React, { useState, useEffect, useCallback } from 'react'
import ODateRangePicker from 'components/shared/datePicker/ODateRangePicker'
import OPagination from 'components/reusable/OPagination'
import dayjs from 'dayjs'
import { apiGet } from '../../../utils/apiFetch'
import apiPath from '../../../utils/apiPath'
import { FaChartBar } from 'react-icons/fa'
import SummaryCards from './SummaryCards'
import ProjectsTable from './ProjectsTable'

const UnitsReport = () => {
  const [loading, setLoading] = useState(false)
  const [projectsLoading, setProjectsLoading] = useState(false)
  const [reportData, setReportData] = useState(null)
  const [projectsData, setProjectsData] = useState(null)
  const [page, setPage] = useState(1)
  const [pageSize] = useState(10)
  const [filterData, setFilterData] = useState({
    startDate: null,
    endDate: null,
    isReset: false
  })
  const [paginationObj, setPaginationObj] = useState({
    page: 1,
    pageCount: 1,
    pageRangeDisplayed: 10
  })
  const [searchInput, setSearchInput] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')

  const { startDate, endDate } = filterData

  const handleDateChange = (start, end) => {
    setPage(1)
    setFilterData({
      ...filterData,
      startDate: start,
      endDate: end
    })
  }

  const handleReset = () => {
    setFilterData({
      startDate: '',
      endDate: '',
      isReset: true
    })
    setPage(1)
    setSearchInput('')
  }

  const handlePageClick = (event) => {
    const newPage = event.selected + 1
    setPage(newPage)
  }

  const handleSetIsReset = (value) => {
    setFilterData(prev => ({ ...prev, isReset: value }))
  }

  const fetchUnitsReport = useCallback(async () => {
    try {
      setLoading(true)
      const payload = {}
      
      if (startDate) payload.startDate = dayjs(startDate).format('YYYY-MM-DD')
      if (endDate) payload.endDate = dayjs(endDate).format('YYYY-MM-DD')
      
      const result = await apiGet(apiPath.getUnitsReportSummary, payload)
      
      if (result?.data?.success) {
        setReportData(result.data.results)
      }
    } catch (error) {
      console.log('Error fetching units report:', error.message)
    } finally {
      setLoading(false)
    }
  }, [startDate, endDate])

  const fetchProjectsData = useCallback(async () => {
    try {
      setProjectsLoading(true)
      const payload = {
        page,
        limit: pageSize,
        startDate: startDate ? dayjs(startDate).format('YYYY-MM-DD') : null,
        endDate: endDate ? dayjs(endDate).format('YYYY-MM-DD') : null,
        search: debouncedSearch || undefined
      }
      
      const result = await apiGet(apiPath.getUnitsReportProjects, payload)
      
      if (result?.data?.success) {
        const response = result.data.results
        setProjectsData(response)
        setPaginationObj(prev => ({
          ...prev,
          page: response.pagination.page,
          pageCount: response.pagination.totalPages,
          perPageItem: response.projects?.length || 0,
          totalItems: response.pagination.total
        }))
      }
    } catch (error) {
      console.log('Error fetching projects data:', error.message)
    } finally {
      setProjectsLoading(false)
    }
  }, [page, pageSize, startDate, endDate, debouncedSearch])

  useEffect(() => {
    fetchUnitsReport()
  }, [fetchUnitsReport])

  useEffect(() => {
    fetchProjectsData()
  }, [fetchProjectsData])

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearch(searchInput.trim())
    }, 500)
    return () => clearTimeout(timeoutId)
  }, [searchInput])

  useEffect(() => {
    setPage(1)
  }, [debouncedSearch])

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Units Report</h1>
        <p className="text-gray-600">Summary report of units with statistics and status breakdown</p>
      </div>

      {/* Date Range Filter */}
      <div className='bg-white'>
        <div className='px-3 py-4'>
          <div className='bg-white border border-[#E9EDF9] '>
            <form className='border-b border-b-[#E3E3E3] 2xl:flex gap-2 px-4 py-3'>
              <div className='flex justify-between w-full'>
                <div className='col-span-2 flex flex-wrap items-center'>
                  <div className='flex items-center lg:pt-0 pt-3 flex-wrap'>
                    <ODateRangePicker
                      handleDateChange={handleDateChange}
                      startDate={startDate}
                      endDate={endDate}
                      isReset={filterData?.isReset}
                      setIsReset={handleSetIsReset}
                    />
                    <input
                      type='text'
                      placeholder='Search project name'
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      className='border border-[#E9EDF9] rounded-lg px-4 py-2 mb-3 lg:ml-3 ml-0 sm:w-64 w-full focus:outline-none focus:ring-2 focus:ring-LightBlue'
                    />
                    <button
                      type='button'
                      onClick={handleReset}
                      className='bg-[#8FC150] text-sm px-8 ml-3 mb-3 py-2 rounded-lg items-center border border-transparent text-white hover:bg-[#8FC150] sm:w-auto w-1/2'
                    >
                      Reset
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      {reportData && (
        <>
          <SummaryCards reportData={reportData} />

          <ProjectsTable data={projectsData} loading={projectsLoading} />

          {/* Pagination */}
          {paginationObj?.totalItems !== 0 && (
            <div className="mt-6">
              <OPagination
                pagination={paginationObj}
                onPageChange={handlePageClick}
                page={page}
              />
            </div>
          )}
        </>
      )}

      {!loading && !reportData && (
        <div className="bg-white p-8 rounded-lg shadow-sm border text-center">
          <FaChartBar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No Data Available
          </h3>
          <p className="text-gray-500">
            No report data found for the selected criteria.
          </p>
        </div>
      )}
    </div>
  )
}

export default UnitsReport